import { useState, useEffect, useCallback, useMemo } from 'react';
import { unitsApi } from '../../api/unitsApi';
import { sortedUnits, unitDisplayName } from '../../Helpers/unitUtils';
import { getDatabase, ref, get } from 'firebase/database';
import { WorkOrderPriority } from '../../types/workOrders';
import { Building, BuildingModel, BuildingSummary } from '../../types/protocols';
import { BaseComponentImpl } from '../../types/implementations';
import { applyComponentPatch, ComponentFieldPatch } from '../../Helpers/componentPatch';
import { useFeature } from '../useFeatures';
import {
    WorkOrderSectionFlags,
    emptyWorkOrderSectionFlags,
    buildEnabledSectionsPayload,
} from '../../Pages/WorkOrders/components/WorkOrderSectionsCheckboxes';

/**
 * The fields shared by every work-order create/edit form. Building selection and
 * assignment are intentionally NOT here — they diverge between the two modals
 * (prop list vs context, internal-only vs internal/external tabs) and stay in the
 * owning shell. `buildingKey` lives here only because the component picker needs it.
 */
export interface WorkOrderFormValues {
    title: string;
    description: string;
    buildingKey: string;
    priority: WorkOrderPriority;
    dueDate: string;
    componentKeys: string[];
    /** Linked lägenhet/lokal (units plan phase 3); empty = none. */
    unitKey: string;
    requiresPhoto: boolean;
    requiresComment: boolean;
    requiresConditionUpdate: boolean;
    enabledSections: WorkOrderSectionFlags;
}

export const emptyWorkOrderFormValues: WorkOrderFormValues = {
    title: '',
    description: '',
    buildingKey: '',
    priority: WorkOrderPriority.MEDIUM,
    dueDate: '',
    componentKeys: [],
    unitKey: '',
    requiresPhoto: true,
    requiresComment: true,
    requiresConditionUpdate: false,
    enabledSections: emptyWorkOrderSectionFlags,
};

/**
 * Shared state + logic for the work-order form. Owns the common field values, the
 * component-picker machinery (loading the full building model, resolving display
 * names), shared validation, and the shared payload subset. The owning modal keeps
 * its own submit path, assignment, and mode-specific concerns and feeds initial
 * values in via `setValues`.
 */
export function useWorkOrderForm() {
    const [values, setValues] = useState<WorkOrderFormValues>(emptyWorkOrderFormValues);
    const [error, setError] = useState<string | null>(null);

    // Component picker state
    const [fullBuilding, setFullBuilding] = useState<Building | null>(null);
    const [loadingFullBuilding, setLoadingFullBuilding] = useState(false);
    const [showComponentPicker, setShowComponentPicker] = useState(false);

    const setField = useCallback(<K extends keyof WorkOrderFormValues>(
        key: K,
        value: WorkOrderFormValues[K]
    ) => {
        setValues(prev => ({ ...prev, [key]: value }));
    }, []);

    const removeComponentKey = useCallback((key: string) => {
        setValues(prev => ({
            ...prev,
            componentKeys: prev.componentKeys.filter(k => k !== key),
        }));
    }, []);

    const { buildingKey } = values;

    // Load the full building model (structure) for the component picker whenever
    // the selected building changes.
    useEffect(() => {
        if (!buildingKey) {
            setFullBuilding(null);
            return;
        }

        let cancelled = false;
        const loadFullBuilding = async () => {
            setLoadingFullBuilding(true);
            try {
                const db = getDatabase();
                const buildingPath = `buildings/${buildingKey}`;
                const [summarySnap, structureSnap, mapImagesSnap, currentMapKeySnap] = await Promise.all([
                    get(ref(db, `buildingsSummary/${buildingKey}`)),
                    get(ref(db, `${buildingPath}/structure`)),
                    get(ref(db, `${buildingPath}/mapImages`)),
                    get(ref(db, `${buildingPath}/currentMapImageKey`)),
                ]);
                if (cancelled) return;
                if (summarySnap.exists()) {
                    setFullBuilding({
                        key: buildingKey,
                        model: {
                            structure: structureSnap.exists() ? structureSnap.val() : undefined,
                            mapImages: mapImagesSnap.exists() ? mapImagesSnap.val() : undefined,
                            currentMapImageKey: currentMapKeySnap.exists() ? currentMapKeySnap.val() : undefined,
                        } as BuildingModel,
                        summary: summarySnap.val() as BuildingSummary,
                    });
                } else {
                    setFullBuilding(null);
                }
            } catch (err) {
                console.error('Failed to load building for component picker:', err);
            } finally {
                if (!cancelled) setLoadingFullBuilding(false);
            }
        };

        loadFullBuilding();
        return () => { cancelled = true; };
    }, [buildingKey]);

    // Resolve a linked component's display name from the loaded building model.
    const componentDisplayName = useCallback((componentKey: string): string => {
        const data = fullBuilding?.model?.structure?.components?.[componentKey];
        if (data && fullBuilding) {
            return new BaseComponentImpl(data).displayName(fullBuilding);
        }
        return 'Komponent';
    }, [fullBuilding]);

    /** Apply an edit saved in the component picker's preview to the loaded building. */
    const patchComponent = useCallback((componentKey: string, patch: ComponentFieldPatch) => {
        setFullBuilding(prev => applyComponentPatch(prev, componentKey, patch));
    }, []);

    /**
     * Validate the shared fields. Returns a Swedish error message, or null when
     * valid. Building is required unless the caller's mode supplies it implicitly
     * (issue/maintenance/project conversions), in which case pass requireBuilding:
     * false.
     */
    const validateShared = useCallback((opts?: { requireBuilding?: boolean }): string | null => {
        if (!values.title.trim()) return 'Titel krävs';
        if ((opts?.requireBuilding ?? true) && !values.buildingKey) return 'Välj en byggnad';
        return null;
    }, [values.title, values.buildingKey]);

    /** Units of the loaded building for the unit picker (apartments first, natural order); none while Lägenheter & lokaler is off. */
    const unitsEnabled = useFeature('units');
    const unitOptions = useMemo(
        () => (unitsEnabled ? sortedUnits(unitsApi.parseUnits(fullBuilding?.model.units)) : []),
        [fullBuilding, unitsEnabled],
    );

    /**
     * Build the payload subset common to every work-order write: normalized
     * component keys (trimmed, deduped), Stockholm-formatted due date, the explicit
     * enabledSections payload, and condition-update gated on having linked
     * components. The owning shell spreads/picks these and adds buildingKey,
     * assignment, projectKey, maintenanceTemplateId, etc.
     */
    const buildSharedFields = useCallback(() => {
        const cleanComponentKeys = Array.from(
            new Set(values.componentKeys.map(k => k.trim()).filter(Boolean))
        );
        const unit = values.unitKey ? unitOptions.find(u => u.key === values.unitKey) : undefined;
        return {
            unitKey: unit?.key,
            unitLabel: unit ? unitDisplayName(unit) : undefined,
            title: values.title.trim(),
            description: values.description.trim() || undefined,
            priority: values.priority,
            dueDate: values.dueDate ? `${values.dueDate} 23:59:59` : undefined,
            // Always an array (possibly empty) so update() can clear the links;
            // create() callers coalesce empty -> undefined where required.
            componentKeys: cleanComponentKeys,
            requiresPhoto: values.requiresPhoto,
            requiresComment: values.requiresComment,
            requiresConditionUpdate: cleanComponentKeys.length > 0 ? values.requiresConditionUpdate : false,
            enabledSections: buildEnabledSectionsPayload(values.enabledSections),
        };
    }, [values, unitOptions]);

    return {
        values,
        unitOptions,
        setValues,
        setField,
        error,
        setError,
        fullBuilding,
        loadingFullBuilding,
        showComponentPicker,
        setShowComponentPicker,
        componentDisplayName,
        patchComponent,
        removeComponentKey,
        validateShared,
        buildSharedFields,
    };
}
